import React, { useState } from 'react';
import LinkButton from '../Button/LinkButton';
import { Theme } from '../../Enums/Theme.enum';
import MediaViewerDisplayImage from './MediaViewerDisplayImage';
import MediaViewerDisplayPdf from './MediaViewerDisplayPdf';
import MediaViewerDisplayNone from './MediaViewerDisplayNone';
import useTranslation from '../../Hooks/useTranslation';
import apiClient from '../../Services/ApiClient';
import useElectronicSignature from '../../Modules/Certificates/Hooks/useElectronicSignature';
import useApiClient from '../../Modules/Base/Hooks/useApiClient';
import ElectronicSignaturePasswordRequestModal from '../../Modules/Certificates/Components/ElectronicSignaturePasswordRequest/ElectronicSignaturePasswordRequestModal';
import useRestApiClient from '../../Modules/Base/Hooks/useRestApiClient';
import useNotification from '../../Hooks/useNotification.hook';
import useJob from '../../Modules/Base/Hooks/useJob';
import useLoading from '../../Modules/Base/Hooks/useLoading';
import SignButton from '../../Modules/Base/Components/SignButton/SignButton';
import ElectronicSignatureRequestModal from '../../Modules/Certificates/Components/ElectronicSignatureRequest/ElectronicSignatureRequestModal';
import { ElectronicSignatureParams } from '../../Modules/Certificates/Components/ElectronicSignatureRequest/ElectronicSignatureRequest';

interface MediaViewerProps {
	file: PreviewFile;
	onSuccess?: () => void;
	params?: any;
	isSignable?: boolean;
	isSignButtonVisible?: boolean;
	setCloseWithReload?: (closeWithReload: boolean) => void;
}

export interface PreviewFile {
	id: string;
	preview: string;
	download: string;
	displayName: string;
	type: string;
	patient?: {
		id: string;
		originalId?: string;
	};
}

export default function MediaViewer(props: MediaViewerProps) {
	const {
		file,
		params,
		onSuccess,
		isSignButtonVisible = true,
		setCloseWithReload,
	} = props;
	const { isLoading, setIsLoading, RenderLoading } = useLoading();
	const [isPasswordRequestOpen, setIsPasswordRequestOpen] = useState(false);
	const [isSignatureRequestOpen, setIsSignatureRequestOpen] = useState(false);
	const [electronicSignatureParams, setElectronicSignatureParams] = useState<
		ElectronicSignatureParams | undefined
	>();
	const { handleStartWaitingForJob, RenderJobWaiting } = useJob();
	const { t } = useTranslation();
	const { displayError } = useApiClient();
	const { showUnexpectedError } = useRestApiClient();
	const { showNotificationSuccess } = useNotification();
	const {
		isPasswordRequiredForElectronicSignatureUsed,
		isLoading: isLoadingElectronicSignatureSettings,
	} = useElectronicSignature();
	const [isSignable, setIsSignable] = useState(props.isSignable ?? false);

	if (!file) {
		return <></>;
	}

	const handlePrint = async () => {
		// @ts-ignore
		const pdfFrame = window.frames[0];
		pdfFrame.focus();
		pdfFrame.print();
	};

	const handleSignDocumentRequest = () => {
		setIsSignatureRequestOpen(true);
	};

	const handleSignDocument = async (params: ElectronicSignatureParams) => {
		setIsSignatureRequestOpen(false);
		if (isPasswordRequiredForElectronicSignatureUsed) {
			setElectronicSignatureParams(params);
			setIsPasswordRequestOpen(true);
			return;
		}
		await handleProcessSign(params);
	};

	const handleOnVerify = async () => {
		setIsPasswordRequestOpen(false);
		if (!electronicSignatureParams) return;
		await handleProcessSign(electronicSignatureParams);
	};

	const handleProcessSign = async (params: ElectronicSignatureParams) => {
		setIsLoading(true);
		try {
			const response = await apiClient.post(
				`/document/${file.id}/sign`,
				params,
			);
			if (response.status === 202 && response.data.jobId) {
				handleStartWaitingForJob(response.data.jobId);
			} else {
				showUnexpectedError();
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	//THIS WILL RELOAD COMPONENT SO IT MAKES GET CALL AGAIN
	const handleRefreshFile = () => {
		setIsLoading(true);
		showNotificationSuccess({
			content: 'component.mediaViewer.documentHasBeenSigned',
		});
		setIsSignable(false);
		setCloseWithReload?.(true);
		setTimeout(() => {
			setIsLoading(false);
		}, 500);
	};

	if (isLoading || isLoadingElectronicSignatureSettings) {
		return <RenderLoading />;
	}

	console.log('file', file);

	return (
		<>
			<div style={{ height: '100%' }}>
				<div className='row'>
					<div className='col-6'>
						{file.type === 'image' && (
							<div className='d-flex align-items-center'>
								<div className='pr-2'>
									<img src='/dist/icons/zoom.svg' alt='zoom icon' />
								</div>
								<div className='text-gray pt-2' style={{ fontSize: '18px' }}>
									{t('component.mediaViewer.imageCanBeZoomed')}
								</div>
							</div>
						)}
					</div>
					<div className='col-6 text-right'>
						<div
							className='d-flex flex-row justify-content-end gap-3'
							style={{ gap: '20px' }}>
							{/*{file.type === 'pdf'  && (*/}
							{/*	<>*/}
							{/*		<LinkButton title={t('base.printWindow.title')} onClick={handlePrint} theme={Theme.BLUE} />*/}
							{/*		<span className='mr-3' />*/}
							{/*	</>*/}
							{/*)}*/}
							{isSignButtonVisible && (
								<SignButton
									onClick={handleSignDocumentRequest}
									isSignAllowed={isSignable}
								/>
							)}
							<LinkButton
								href={file.download}
								title={t('component.mediaViewer.downloadDocument') ?? ''}
								firstUpper
								theme={Theme.BLUE}
							/>
						</div>
					</div>
				</div>
				{file.type === 'image' && <MediaViewerDisplayImage file={file} />}
				{file.type === 'pdf' && <MediaViewerDisplayPdf file={file} />}
				{file.type === 'none' && <MediaViewerDisplayNone file={file} />}
			</div>
			{isSignatureRequestOpen && file.patient && (
				<ElectronicSignatureRequestModal
					isOpen
					onClose={() => setIsSignatureRequestOpen(false)}
					onSign={handleSignDocument}
					userId={file.patient.originalId ?? file.patient.id}
				/>
			)}
			{isPasswordRequestOpen && (
				<ElectronicSignaturePasswordRequestModal
					isOpen
					onClose={() => setIsPasswordRequestOpen(false)}
					onVerify={handleOnVerify}
					submitButtonText='base.sign'
				/>
			)}
			<RenderJobWaiting
				onJobSuccess={handleRefreshFile}
				waitingMessage='component.mediaViewer.documentIsSigning'
				failedMessage='component.mediaViewer.documentSignFailed'
				timeout={180000}
			/>
		</>
	);
}
